import InfoIcon from '../icons/InfoIcon';
import { Box, ClickAwayListener } from '@mui/material';
import { DELIVERY_FEE } from '../../constants/cart';
import { FormattedMessage } from 'react-intl';
import { PropsWithChildren, useState } from 'react';
import { TooltipType } from '../../types/tooltip';
import {
  MUITooltip,
  TooltipButton,
  TooltipContentWrapper,
  TooltipIcon,
  TooltipIconWrapper,
  TooltipText,
} from './Style';

interface ChargesInfoTooltipProps {
  selector: TooltipType;
}

const LEAVE_DELAY = 300;
const LEAVE_TOUCH_DELAY = 30 * 1000;

/**
 * Tooltip for Rental Protection Plan.
 */
const RentalProtectionPlanTooltip = () => {
  return (
    <TooltipContent>
      {`Secure your rental investments with EquipmentShare's Rental Protection Plan, designed to limit
        your liability to just $1,000 in cases of loss, theft, or damage to rented equipment. To learn
        more, see the EquipmentShare `}
      <a
        target='_blank'
        rel='noreferrer'
        href='https://www.equipmentshare.com/rental-protection-plan'
      >
        Rental Protection Plan Terms
      </a>
      .
    </TooltipContent>
  );
};

/**
 * Tooltip for Equipment Charges.
 */
const EquipmentChargesTooltip = () => {
  return (
    <TooltipContent>
      Equipment charges can include environmental fees and other charges based on location or
      equipment model rented.
    </TooltipContent>
  );
};

/**
 * Tooltip for Delivery Charges.
 */
const EstimatedDeliveryFeeTooltip = () => {
  return (
    <TooltipContent>
      {`Estimated delivery fee is $${DELIVERY_FEE} per trip. Select "I'll pick up/drop off" to avoid fee.`}
    </TooltipContent>
  );
};

/**
 * Tooltip for Credit Application Denied.
 */
const CreditApplicationDeniedTooltip = () => {
  return (
    <TooltipContent>
      <FormattedMessage id='creditApplication.deniedTooltip' />
    </TooltipContent>
  );
};

/**
 * Tooltip for Document Not Approved.
 */
const DocumentPendingTooltip = () => {
  return (
    <TooltipContent>
      Your document is being scanned by the system. Please check back later or contact support if
      the status remains pending for more than a day. If your upload fails, you’ll receive an email
      with instructions to resolve the issue.
    </TooltipContent>
  );
};

/**
 * Tooltip for Document Not Approved.
 */
const DocumentNotApprovedTooltip = () => {
  return (
    <TooltipContent>
      Document requires changes or additional information before approval
    </TooltipContent>
  );
};

/**
 * Tooltip content component.
 */
const TooltipContent = ({ children }: PropsWithChildren) => {
  return (
    <TooltipContentWrapper>
      <TooltipIconWrapper>
        <InfoIcon className='tooltip-icon' />
      </TooltipIconWrapper>
      <TooltipText>{children}</TooltipText>
    </TooltipContentWrapper>
  );
};

/**
 * Tooltip component that provides tooltip with content for the specific
 * summary line item based on the passed selector.
 */
function Tooltip({ selector, ...rest }: ChargesInfoTooltipProps) {
  const [open, setOpen] = useState(false);

  const handleTooltipClose = () => {
    setOpen(false);
  };

  const handleTooltipOpen = () => {
    setOpen(true);
  };

  const getTooltipContent = () => {
    switch (selector) {
      case TooltipType.RENTAL_PROTECTION_PLAN_TOOLTIP:
        return <RentalProtectionPlanTooltip />;
      case TooltipType.EQUIPMENT_CHARGES_TOOLTIP:
        return <EquipmentChargesTooltip />;
      case TooltipType.ESTIMATED_DELIVERY_FEE_TOOLTIP:
        return <EstimatedDeliveryFeeTooltip />;
      case TooltipType.CREDIT_APPLICATION_DENIED_TOOLTIP:
        return <CreditApplicationDeniedTooltip />;
      case TooltipType.DOCUMENT_PENDING_TOOLTIP:
        return <DocumentPendingTooltip />;
      case TooltipType.DOCUMENT_NOT_APPROVED_TOOLTIP:
        return <DocumentNotApprovedTooltip />;
      default:
        return <></>;
    }
  };

  return (
    <ClickAwayListener onClickAway={handleTooltipClose}>
      <Box className='tooltip-container' {...rest}>
        <MUITooltip
          arrow
          open={open}
          onClose={handleTooltipClose}
          onOpen={handleTooltipOpen}
          title={getTooltipContent()}
          leaveDelay={LEAVE_DELAY}
          leaveTouchDelay={LEAVE_TOUCH_DELAY}
        >
          <TooltipButton disableRipple onClick={handleTooltipOpen}>
            <TooltipIcon className='tooltip-icon' />
          </TooltipButton>
        </MUITooltip>
      </Box>
    </ClickAwayListener>
  );
}

export default Tooltip;

export const getTooltip = (selector: TooltipType) => {
  return <Tooltip selector={selector} />;
};
